import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import './Agents.css';
import { getAgents, getMyAgents } from '../../../api/services/agentService';
import { assignAgentToProperty, getMyProperties, updatePropertyAgentRole } from '../../../api/services/propertyService';

const INVITES_STORAGE_KEY = 'airbnb_agent_property_invites_v1';

const loadInvites = () => {
  try {
    const raw = localStorage.getItem(INVITES_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch (_) {
    return [];
  }
};

const saveInvites = (invites) => {
  try {
    localStorage.setItem(INVITES_STORAGE_KEY, JSON.stringify(invites));
  } catch (_) {
    // ignore
  }
};

const getAgentDisplayName = (agent) => {
  const first = agent?.firstName?.trim() || '';
  const last = agent?.lastName?.trim() || '';
  const full = `${first} ${last}`.trim();
  return full || agent?.email || 'Agent';
};

const formatRoleLabel = (rawRole) => {
  const role = String(rawRole || '').toUpperCase().trim();
  if (role === 'MANAGER') return 'Manager';
  if (role === 'VIEWER') return 'Viewer';
  return role ? role.charAt(0) + role.slice(1).toLowerCase() : '';
};

const normalizeAssignmentStatus = (rawStatus, rawIsActive) => {
  if (typeof rawIsActive === 'boolean') return rawIsActive ? 'ACTIVE' : 'PENDING';
  const s = String(rawStatus || '').toUpperCase().trim();
  if (!s) return '';
  if (s === 'ACTIVE') return 'ACTIVE';
  if (s === 'PENDING') return 'PENDING';
  if (s === 'REJECTED' || s === 'DECLINED') return 'REJECTED';
  return s;
};

const normalizeInviteResolution = (rawStatus, rawIsActive) => {
  const status = normalizeAssignmentStatus(rawStatus, rawIsActive);
  if (status === 'ACTIVE') return 'APPROVED';
  if (status === 'REJECTED') return 'REJECTED';
  return '';
};

const resolveInviteFromMyAgentsResponse = (items, invite) => {
  const list = Array.isArray(items) ? items : [];
  for (const agentEntry of list) {
    const agentId = agentEntry?.agentId || agentEntry?.agent?.id || agentEntry?.id;
    if (invite?.agentId && agentId && invite.agentId !== agentId) continue;

    const assignments = Array.isArray(agentEntry?.assignments) ? agentEntry.assignments : [];
    for (const a of assignments) {
      const assignmentId = a?.id || a?.assignmentId;
      const propertyId = a?.propertyId || a?.property?.id;
      const matchesByAssignmentId = invite?.assignmentId && assignmentId && invite.assignmentId === assignmentId;
      const matchesByPropertyId = invite?.propertyId && propertyId && invite.propertyId === propertyId;

      if (!matchesByAssignmentId && !matchesByPropertyId) continue;

      const resolved = normalizeInviteResolution(a?.status, a?.isActive);
      if (resolved) return resolved;
    }
  }
  return '';
};

const Agents = () => {
  const [agents, setAgents] = useState([]);
  const [myAgentsDetailsById, setMyAgentsDetailsById] = useState(() => new Map());
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');
  const [search, setSearch] = useState('');
  const [filterMode, setFilterMode] = useState('all'); // all | my
  const [page, setPage] = useState(0);
  const [size] = useState(20);
  const [pageInfo, setPageInfo] = useState({ totalPages: 1, totalElements: 0 });
  const [myAgentIds, setMyAgentIds] = useState(new Set());
  const [agentInvites, setAgentInvites] = useState(() => loadInvites()); // [{agentId, propertyId, status}]
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [assigning, setAssigning] = useState(false);
  const [assignError, setAssignError] = useState('');
  const [assignSuccess, setAssignSuccess] = useState('');
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [myProperties, setMyProperties] = useState([]);
  const [loadingProperties, setLoadingProperties] = useState(false);
  const [selectedPropertyAgents, setSelectedPropertyAgents] = useState([]);
  const [loadingSelectedPropertyAgents, setLoadingSelectedPropertyAgents] = useState(false);
  const [selectedPropertyAgentsError, setSelectedPropertyAgentsError] = useState('');
  const [showRoleModal, setShowRoleModal] = useState(false);
  const [editingAssignment, setEditingAssignment] = useState(null);
  const [roleFormValue, setRoleFormValue] = useState('VIEWER');
  const [updatingRole, setUpdatingRole] = useState(false);
  const [roleUpdateError, setRoleUpdateError] = useState('');
  const [assignForm, setAssignForm] = useState({
    propertyId: '',
    role: 'MANAGER',
    notes: '',
  });

  useEffect(() => {
    const fetchAllAgents = async () => {
      setLoading(true);
      setErrorMessage('');
      try {
        const response = await getAgents({ page, size, search });
        const content = response?.data?.content || [];
        setAgents(Array.isArray(content) ? content : []);
        setPageInfo({
          totalPages: response?.data?.totalPages ?? 1,
          totalElements: response?.data?.totalElements ?? content.length,
        });
      } catch (error) {
        console.error('Error fetching agents:', error);
        setErrorMessage(error.message || 'Failed to load agents. Please try again.');
        setAgents([]);
        setPageInfo({ totalPages: 1, totalElements: 0 });
      } finally {
        setLoading(false);
      }
    };

    fetchAllAgents();
  }, [page, size, search]);

  useEffect(() => {
    // "My agents" = agents returned by /api/agents/my-agents
    const fetchMyAgentIds = async () => {
      try {
        const response = await getMyAgents();
        const myAgents = response?.data || [];
        const ids = new Set();
        const detailsMap = new Map();
        (Array.isArray(myAgents) ? myAgents : []).forEach((entry) => {
          const agentId = entry?.agentId || entry?.agent?.id || entry?.id;
          if (agentId) {
            ids.add(agentId);
            detailsMap.set(agentId, entry);
          }
        });
        setMyAgentIds(ids);
        setMyAgentsDetailsById(detailsMap);

        // Reconcile pending invites using my-agents assignments statuses
        setAgentInvites((prev) => {
          if (!Array.isArray(prev) || prev.length === 0) return prev;
          const next = prev.map((inv) => {
            if (inv?.status !== 'PENDING') return inv;
            const resolved = resolveInviteFromMyAgentsResponse(myAgents, inv);
            return resolved ? { ...inv, status: resolved } : inv;
          });
          saveInvites(next);
          return next;
        });
      } catch (error) {
        console.error('Error fetching my agents for agent filter:', error);
        setMyAgentIds(new Set());
        setMyAgentsDetailsById(new Map());
      }
    };

    fetchMyAgentIds();
  }, []);

  const pendingInviteAgentIds = useMemo(() => {
    const ids = new Set();
    (Array.isArray(agentInvites) ? agentInvites : []).forEach((inv) => {
      if (inv?.status === 'PENDING' && inv?.agentId) ids.add(inv.agentId);
    });
    return ids;
  }, [agentInvites]);

  const openAssignModal = async (agent) => {
    setSelectedAgent(agent);
    setAssignError('');
    setAssignSuccess('');
    setAssignForm({ propertyId: '', role: 'MANAGER', notes: '' });
    setSelectedPropertyAgents([]);
    setSelectedPropertyAgentsError('');
    setShowAssignModal(true);

    setLoadingProperties(true);
    try {
      // Pass dashboardScope: 'BNB' to filter for Airbnb properties
      const response = await getMyProperties({ page: 0, size: 200, search: '', dashboardScope: 'BNB' });
      const props = response?.data?.content || [];
      setMyProperties(Array.isArray(props) ? props : []);
    } catch (error) {
      console.error('Error fetching properties:', error);
      setMyProperties([]);
      setAssignError(error.message || 'Failed to load properties. Please try again.');
    } finally {
      setLoadingProperties(false);
    }
  };

  useEffect(() => {
    const propertyId = assignForm?.propertyId;
    if (!showAssignModal || !propertyId) {
      setSelectedPropertyAgents([]);
      setSelectedPropertyAgentsError('');
      return;
    }

    let cancelled = false;
    const fetchPropertyAgents = async () => {
      setLoadingSelectedPropertyAgents(true);
      setSelectedPropertyAgentsError('');
      try {
        // Use the scoped endpoint: GET /api/agents/my-agents?propertyId=xxx
        const res = await getMyAgents({ propertyId });
        const raw = res?.data || [];
        // Flatten into rows that match the panel's render shape
        const normalised = (Array.isArray(raw) ? raw : []).flatMap((entry) => {
          const base = {
            agentId: entry.agentId,
            agentName: entry.agentName,
            agentEmail: entry.agentEmail,
            agentPhone: entry.agentPhone,
          };
          const assignments = Array.isArray(entry.assignments) ? entry.assignments : [];
          if (assignments.length === 0) return [{ ...base, id: entry.agentId }];
          return assignments.map((a) => ({
            ...base,
            id: a.id,
            role: a.role,
            status: a.status,
            isActive: a.isActive,
            assignedByName: a.assignedByName,
            propertyId: a.propertyId,
            propertyName: a.propertyName,
          }));
        });
        if (!cancelled) setSelectedPropertyAgents(normalised);
      } catch (e) {
        if (!cancelled) {
          setSelectedPropertyAgents([]);
          setSelectedPropertyAgentsError(e?.message || 'Failed to load property agents');
        }
      } finally {
        if (!cancelled) setLoadingSelectedPropertyAgents(false);
      }
    };

    fetchPropertyAgents();
    return () => {
      cancelled = true;
    };
  }, [assignForm?.propertyId, showAssignModal]);

  const refreshMyAgentIds = async () => {
    try {
      const response = await getMyAgents();
      const myAgents = response?.data || [];
      const ids = new Set();
      const detailsMap = new Map();
      (Array.isArray(myAgents) ? myAgents : []).forEach((entry) => {
        const agentId = entry?.agentId || entry?.agent?.id || entry?.id;
        if (agentId) {
          ids.add(agentId);
          detailsMap.set(agentId, entry);
        }
      });
      setMyAgentIds(ids);
      setMyAgentsDetailsById(detailsMap);

      setAgentInvites((prev) => {
        if (!Array.isArray(prev) || prev.length === 0) return prev;
        const next = prev.map((inv) => {
          if (inv?.status !== 'PENDING') return inv;
          const resolved = resolveInviteFromMyAgentsResponse(myAgents, inv);
          return resolved ? { ...inv, status: resolved } : inv;
        });
        saveInvites(next);
        return next;
      });
    } catch (_) {
      // ignore
    }
  };

  const isDuplicateAssignment = (agentId, propertyId) => {
    if (!agentId || !propertyId) return false;
    const prop = myProperties.find((p) => p.id === propertyId);
    const assignedId =
      prop?.assignedAgentId ||
      prop?.assignedAgent?.id ||
      prop?.agentId ||
      prop?.agent?.id;
    if (assignedId && assignedId === agentId) return true;

    return (Array.isArray(agentInvites) ? agentInvites : []).some(
      (inv) => inv?.agentId === agentId && inv?.propertyId === propertyId && inv?.status !== 'REJECTED'
    );
  };

  const formatAssignmentStatus = (status, isActive) => {
    if (typeof isActive === 'boolean') return isActive ? 'ACTIVE' : (status || 'PENDING');
    return status || 'PENDING';
  };

  const isApiSuccess = (res) => {
    if (!res) return false;
    if (typeof res.success === 'boolean') return res.success;
    if (typeof res.status === 'string') return res.status.toLowerCase() === 'success';
    return true;
  };

  const getAssignmentId = (row) => row?.assignmentId || row?.id || row?.agentAssignmentId || null;

  const getAssignmentPropertyId = (row) => row?.propertyId || row?.property?.id || null;

  const openRoleModal = (row) => {
    const assignmentId = getAssignmentId(row);
    const propertyId = getAssignmentPropertyId(row);
    if (!assignmentId || !propertyId) return;
    const role = String(row?.role || '').toUpperCase().trim();
    setEditingAssignment({
      assignmentId,
      propertyId,
      propertyName: row?.propertyName || row?.property?.name || 'Property',
      agentName: row?.agentName || row?.agentEmail || 'Agent',
      currentRole: role === 'MANAGER' ? 'MANAGER' : 'VIEWER',
    });
    setRoleFormValue(role === 'MANAGER' ? 'MANAGER' : 'VIEWER');
    setRoleUpdateError('');
    setShowRoleModal(true);
  };

  const handleRoleUpdateSubmit = async (e) => {
    e.preventDefault();
    if (!editingAssignment?.propertyId || !editingAssignment?.assignmentId) return;
    setRoleUpdateError('');
    setUpdatingRole(true);

    try {
      const res = await updatePropertyAgentRole(editingAssignment.propertyId, editingAssignment.assignmentId, {
        role: roleFormValue,
      });
      if (!isApiSuccess(res)) {
        throw new Error(res?.message || 'Failed to update role');
      }

      if (showAssignModal && assignForm?.propertyId && assignForm.propertyId === editingAssignment.propertyId) {
        const refreshRes = await getMyAgents({ propertyId: assignForm.propertyId });
        const raw = refreshRes?.data || [];
        const normalised = (Array.isArray(raw) ? raw : []).flatMap((entry) => {
          const base = {
            agentId: entry.agentId,
            agentName: entry.agentName,
            agentEmail: entry.agentEmail,
            agentPhone: entry.agentPhone,
          };
          const asgns = Array.isArray(entry.assignments) ? entry.assignments : [];
          if (asgns.length === 0) return [{ ...base, id: entry.agentId }];
          return asgns.map((a) => ({
            ...base,
            id: a.id,
            role: a.role,
            status: a.status,
            isActive: a.isActive,
            assignedByName: a.assignedByName,
            propertyId: a.propertyId,
            propertyName: a.propertyName,
          }));
        });
        setSelectedPropertyAgents(normalised);
      }
      await refreshMyAgentIds();
      setShowRoleModal(false);
      setEditingAssignment(null);
    } catch (error) {
      console.error('Error updating assignment role:', error);
      setRoleUpdateError(error?.message || 'Failed to update role. Please try again.');
    } finally {
      setUpdatingRole(false);
    }
  };

  const handleAssignSubmit = async (e) => {
    e.preventDefault();
    setAssignError('');
    setAssignSuccess('');

    if (!selectedAgent?.id) {
      setAssignError('Agent is missing. Please try again.');
      return;
    }
    if (!assignForm.propertyId) {
      setAssignError('Please select a property.');
      return;
    }
    if (isDuplicateAssignment(selectedAgent.id, assignForm.propertyId)) {
      setAssignError('This agent is already assigned (or already invited) for the selected property.');
      return;
    }

    setAssigning(true);
    try {
      const payload = {
        agentId: selectedAgent.id,
        role: assignForm.role,
      };
      if (assignForm.notes && assignForm.notes.trim()) {
        payload.notes = assignForm.notes.trim();
      }

      const response = await assignAgentToProperty(assignForm.propertyId, payload);
      if (response?.status === 'success') {
        setAssignSuccess(response.message || 'Agent invited to manage property');
        setAgentInvites((prev) => {
          const current = Array.isArray(prev) ? prev : [];
          const next = current.filter((inv) => !(inv?.agentId === selectedAgent.id && inv?.propertyId === assignForm.propertyId));
          const assignmentId =
            response?.data?.id ||
            response?.data?.assignmentId ||
            response?.data?.data?.id ||
            response?.data?.data?.assignmentId ||
            response?.id ||
            null;
          next.unshift({ agentId: selectedAgent.id, propertyId: assignForm.propertyId, assignmentId, status: 'PENDING' });
          saveInvites(next);
          return next;
        });
        await refreshMyAgentIds();
        setShowAssignModal(false);
        setSelectedAgent(null);
      } else {
        throw new Error(response?.message || 'Failed to assign agent to property');
      }
    } catch (error) {
      console.error('Error assigning agent:', error);
      setAssignError(error.message || 'Failed to assign agent. Please try again.');
    } finally {
      setAssigning(false);
    }
  };

  const visibleAgents = useMemo(() => {
    if (filterMode === 'my') {
      return agents.filter((a) => myAgentIds.has(a.id));
    }
    return agents;
  }, [agents, filterMode, myAgentIds]);

  const getAgentAssignmentsPreview = (agentId) => {
    const entry = myAgentsDetailsById?.get?.(agentId);
    const assignments = Array.isArray(entry?.assignments) ? entry.assignments : [];
    return assignments;
  };

  return (
    <div className="agents-container">
      <div className="agents-header">
        <div>
          <h1 className="page-title">Agents</h1>
          <p className="page-subtitle">
            View and manage agents assigned to your BNB properties.
          </p>
        </div>
        <div className="agents-header-actions">
          <select
            className="agent-filter-dropdown"
            value={filterMode}
            onChange={(e) => setFilterMode(e.target.value)}
          >
            <option value="all">All Agents</option>
            <option value="my">My Agents</option>
          </select>

          <div className="search-container">
            <input
              type="text"
              className="search-input"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(0);
              }}
              placeholder="Search by name, email, phone, or ID..."
            />
          </div>
        </div>
      </div>

      {errorMessage && (
        <div className="empty-state" style={{ backgroundColor: '#fee2e2', color: '#991b1b' }}>
          {errorMessage}
        </div>
      )}

      {loading ? (
        <div className="empty-state">Loading agents...</div>
      ) : visibleAgents.length === 0 ? (
        <div className="empty-state">
          {filterMode === 'my' ? 'No agents assigned to your properties yet.' : 'No agents found.'}
        </div>
      ) : (
        <div className="agents-grid">
          {visibleAgents.map((agent) => (
            <div key={agent.id} className="agent-card card">
              <div className="agent-card-header">
                <div className="agent-name">{getAgentDisplayName(agent)}</div>
                <span className={`status-badge ${agent.isActive ? 'status-active' : 'status-pending_kyc'}`}>
                  {agent.isActive ? 'Active' : 'Inactive'}
                </span>
              </div>

              <div className="agent-meta agent-meta-compact">
                {agent.email && (
                  <div className="agent-meta-row">
                    <span className="agent-meta-label">Email</span>
                    <span className="agent-meta-value">{agent.email}</span>
                  </div>
                )}
                {agent.phone && (
                  <div className="agent-meta-row">
                    <span className="agent-meta-label">Phone</span>
                    <span className="agent-meta-value">{agent.phone}</span>
                  </div>
                )}

                {filterMode === 'my' && getAgentAssignmentsPreview(agent.id).length > 0 && (
                  <div className="agent-assignments-preview">
                    <div className="agent-assignments-title">Assigned to</div>
                    <div className="agent-assignments-list">
                      {getAgentAssignmentsPreview(agent.id).slice(0, 2).map((a) => (
                        <div key={a.id || `${a.propertyId}-${a.assignedAt || ''}`} className="agent-assignment-chip">
                          <div className="agent-assignment-row">
                            <div className="agent-assignment-name">{a.propertyName || 'Property'}</div>
                            <button
                              type="button"
                              className="btn btn-secondary btn-sm agent-assignment-role-btn"
                              onClick={() => openRoleModal({
                                ...a,
                                agentName: getAgentDisplayName(agent),
                              })}
                              disabled={updatingRole}
                            >
                              Change role
                            </button>
                          </div>
                          <div className="agent-assignment-location">{a.propertyAddress || '—'}</div>
                          {a.role && (
                            <div className="agent-assignment-role-line">
                              Role: <strong>{formatRoleLabel(a.role)}</strong>
                            </div>
                          )}
                        </div>
                      ))}
                      {getAgentAssignmentsPreview(agent.id).length > 2 && (
                        <div className="agent-assignment-more">
                          +{getAgentAssignmentsPreview(agent.id).length - 2} more
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>

              <div className="agent-kyc-status agent-kyc-compact">
                <span className="kyc-label">KYC</span>
                <span className={`kyc-badge kyc-${(agent.kycStatus || 'PENDING').toLowerCase()}`}>
                  {agent.kycStatus || 'PENDING'}
                </span>
              </div>

              <div className="agent-card-actions">
                <button
                  type="button"
                  className="btn btn-secondary btn-sm"
                  onClick={() => openAssignModal(agent)}
                  disabled={pendingInviteAgentIds.has(agent.id)}
                >
                  {pendingInviteAgentIds.has(agent.id)
                    ? 'Pending invitation'
                    : 'Assign to property'}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {!loading && !errorMessage && pageInfo.totalPages > 1 && (
        <div className="agents-pagination">
          <small className="form-hint" style={{ margin: 0 }}>
            Page {page + 1} of {pageInfo.totalPages} • {pageInfo.totalElements} total
          </small>
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              disabled={page === 0}
            >
              Prev
            </button>
            <button
              type="button"
              className="btn btn-secondary btn-sm"
              onClick={() => setPage((p) => Math.min(pageInfo.totalPages - 1, p + 1))}
              disabled={page >= pageInfo.totalPages - 1}
            >
              Next
            </button>
          </div>
        </div>
      )}

      {showAssignModal && createPortal(
        <div className="modal-overlay" onClick={() => !assigning && setShowAssignModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Assign agent to property</h2>
              <button className="close-btn" onClick={() => !assigning && setShowAssignModal(false)} disabled={assigning}>×</button>
            </div>

            <div style={{ padding: '16px 24px 0' }}>
              <div className="form-hint" style={{ margin: 0 }}>
                Agent: <strong>{getAgentDisplayName(selectedAgent)}</strong>
              </div>
            </div>

            {assignError && (
              <div style={{ padding: '12px', margin: '16px 24px 0', backgroundColor: '#fee2e2', color: '#991b1b', borderRadius: '8px' }}>
                {assignError}
              </div>
            )}
            {assignSuccess && (
              <div style={{ padding: '12px', margin: '16px 24px 0', backgroundColor: '#dcfce7', color: '#166534', borderRadius: '8px' }}>
                {assignSuccess}
              </div>
            )}

            <form onSubmit={handleAssignSubmit} className="agent-form">
              <div className="form-group">
                <label>Property *</label>
                <select
                  value={assignForm.propertyId}
                  onChange={(e) => setAssignForm({ ...assignForm, propertyId: e.target.value })}
                  disabled={assigning || loadingProperties}
                  required
                >
                  <option value="">{loadingProperties ? 'Loading properties...' : 'Select a property'}</option>
                  {myProperties.map((p) => {
                    const disabled = isDuplicateAssignment(selectedAgent?.id, p.id);
                    return (
                      <option key={p.id} value={p.id} disabled={disabled}>
                        {p.name}{p.address ? ` — ${p.address}` : ''}{disabled ? ' (already assigned/invited)' : ''}
                      </option>
                    );
                  })}
                </select>
              </div>

              {assignForm.propertyId && (
                <div className="property-agents-panel">
                  <div className="property-agents-panel-header">
                    <div className="property-agents-panel-title">Agents managing this property</div>
                    {loadingSelectedPropertyAgents && (
                      <div className="property-agents-panel-subtitle">Loading…</div>
                    )}
                  </div>

                  {selectedPropertyAgentsError && (
                    <div className="property-agents-panel-error">{selectedPropertyAgentsError}</div>
                  )}

                  {!loadingSelectedPropertyAgents && !selectedPropertyAgentsError && (
                    selectedPropertyAgents.length === 0 ? (
                      <div className="property-agents-panel-empty">No agents assigned to this property yet.</div>
                    ) : (
                      <div className="property-agents-list">
                        {selectedPropertyAgents.map((a) => (
                          <div key={a.id || `${a.agentId}-${a.propertyId}`} className="property-agent-row">
                            <div className="property-agent-main">
                              <div className="property-agent-name">{a.agentName || a.agentEmail || 'Agent'}</div>
                              <div className="property-agent-meta">
                                {a.agentEmail && <span>{a.agentEmail}</span>}
                                {a.agentPhone && <span>{a.agentPhone}</span>}
                                {a.role && (
                                  <span className={`property-agent-role-chip property-agent-role-chip--${(a.role || '').toLowerCase()}`}>
                                    {formatRoleLabel(a.role)}
                                  </span>
                                )}
                                {a.assignedByName && (
                                  <span className="property-agent-assigned-by">Assigned by {a.assignedByName}</span>
                                )}
                              </div>
                            </div>
                            <div className="property-agent-actions">
                              <span
                                className={`status-badge ${
                                  String(formatAssignmentStatus(a.status, a.isActive)).toUpperCase() === 'ACTIVE'
                                    ? 'status-active'
                                    : 'status-pending_kyc'
                                }`}
                              >
                                {formatAssignmentStatus(a.status, a.isActive)}
                              </span>
                              {!!getAssignmentId(a) && (
                                <button
                                  type="button"
                                  className="btn btn-secondary btn-sm"
                                  onClick={() => openRoleModal(a)}
                                  disabled={assigning || loadingSelectedPropertyAgents}
                                >
                                  Change role
                                </button>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )
                  )}
                </div>
              )}

              <div className="form-row">
                <div className="form-group">
                  <label>Role *</label>
                  <select
                    value={assignForm.role}
                    onChange={(e) => setAssignForm({ ...assignForm, role: e.target.value })}
                    disabled={assigning}
                    required
                  >
                    <option value="MANAGER">MANAGER</option>
                    <option value="VIEWER">VIEWER</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Notes (optional)</label>
                  <input
                    type="text"
                    value={assignForm.notes}
                    onChange={(e) => setAssignForm({ ...assignForm, notes: e.target.value })}
                    placeholder="e.g., Manage rentals and viewings"
                    disabled={assigning}
                  />
                </div>
              </div>

              <div className="form-actions">
                <button type="button" className="btn btn-secondary" onClick={() => setShowAssignModal(false)} disabled={assigning}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={assigning || loadingProperties}>
                  {assigning ? 'Sending...' : 'Send request'}
                </button>
              </div>
            </form>
          </div>
        </div>,
        document.body
      )}

      {showRoleModal && createPortal(
        <div className="modal-overlay" onClick={() => !updatingRole && setShowRoleModal(false)}>
          <div className="modal-content role-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Change agent role</h2>
              <button
                className="close-btn"
                onClick={() => !updatingRole && setShowRoleModal(false)}
                disabled={updatingRole}
              >
                ×
              </button>
            </div>

            <form onSubmit={handleRoleUpdateSubmit} className="agent-form">
              <div className="form-hint" style={{ margin: 0 }}>
                Agent: <strong>{editingAssignment?.agentName || 'Agent'}</strong>
              </div>
              <div className="form-hint" style={{ marginTop: 4 }}>
                Property: <strong>{editingAssignment?.propertyName || 'Property'}</strong>
              </div>
              <div className="form-hint" style={{ marginTop: 4 }}>
                Current role: <strong>{formatRoleLabel(editingAssignment?.currentRole)}</strong>
              </div>

              {roleUpdateError && (
                <div style={{ padding: '12px', marginTop: '8px', backgroundColor: '#fee2e2', color: '#991b1b', borderRadius: '8px' }}>
                  {roleUpdateError}
                </div>
              )}

              <div className="form-group">
                <label>New role *</label>
                <select
                  value={roleFormValue}
                  onChange={(e) => setRoleFormValue(e.target.value)}
                  disabled={updatingRole}
                  required
                >
                  <option value="MANAGER">MANAGER</option>
                  <option value="VIEWER">VIEWER</option>
                </select>
              </div>

              <div className="form-actions">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowRoleModal(false)}
                  disabled={updatingRole}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={updatingRole}
                >
                  {updatingRole ? 'Updating...' : 'Update role'}
                </button>
              </div>
            </form>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default Agents;
